import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import Badge from "../components/ui/Badge";
import DashboardLayout from "../components/layout/DashboardLayout";
import NotificationSettings from "../components/NotificationSettings";
import NotificationTest from "../components/NotificationTest";
import { useAuth } from "../context/AuthContext";
import { getSettings, updateSettings } from "../services/api";

export default function SettingsPage() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  
  // Settings state
  const [settings, setSettings] = useState({
    check_interval: 120,
    timeout_seconds: 30,
    alert_cooldown: 600,
    performance_tracking: true,
    uptime_history: true,
    email_notifications: true,
    push_notifications: true,
    sms_notifications: false
  });
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Load settings on component mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await getSettings();
      if (res.ok) {
        setSettings(res.data);
        console.log("✅ Settings loaded:", res.data);
      } else {
        setError(res.error || "Failed to load settings");
      }
    } catch (err) {
      setError("Failed to connect to server");
      console.error("Load settings error:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    localStorage.removeItem("push_registered_v1");
    navigate("/login");
  };

  // Handle form changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : parseInt(value, 10)
    }));
  };

  // Save monitoring settings
  const handleSaveMonitoringSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");
    
    try {
      const res = await updateSettings({
        check_interval: settings.check_interval,
        timeout_seconds: settings.timeout_seconds,
        alert_cooldown: settings.alert_cooldown,
        performance_tracking: settings.performance_tracking,
        uptime_history: settings.uptime_history
      });
      
      if (res.ok) {
        setMessage("Monitoring settings saved successfully!");
        console.log("✅ Monitoring settings updated:", res.data);
      } else {
        setError(res.error || "Failed to save monitoring settings");
      }
    } catch (err) {
      setError("Failed to connect to server");
      console.error("Save monitoring settings error:", err);
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(""), 3000);
    }
  };

  // Save notification settings
  const handleSaveNotificationSettings = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage("");
    setError("");
    
    try {
      const res = await updateSettings({
        email_notifications: settings.email_notifications,
        push_notifications: settings.push_notifications,
        sms_notifications: settings.sms_notifications
      });
      
      if (res.ok) {
        setMessage("Notification settings saved successfully!");
        console.log("✅ Notification settings updated:", res.data);
      } else {
        setError(res.error || "Failed to save notification settings");
      }
    } catch (err) {
      setError("Failed to connect to server");
      console.error("Save notification settings error:", err);
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(""), 3000);
    }
  };



  // Export user data
  const handleExportData = async () => {
    setMessage("");
    setError("");
    
    try {
      // Call the new comprehensive export endpoint
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:4000/api/export-user-data', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        
        // Create and download the JSON file
        const dataStr = JSON.stringify(data, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `sitescope-data-export-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        setMessage("✅ Data exported successfully! Your download should start automatically.");
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to export data");
      }
    } catch (err) {
      setError("Failed to connect to server for data export");
      console.error("Export data error:", err);
    } finally {
      setTimeout(() => setMessage(""), 5000);
    }
  };

  // Delete account with confirmation
  const handleDeleteAccount = async () => {
    setMessage("");
    setError("");

    // First confirmation
    const confirmDelete = window.confirm(
      "⚠️ WARNING: This will permanently delete your account and all data.\n\n" +
      "This action cannot be undone. Are you sure you want to continue?"
    );

    if (!confirmDelete) return;

    // Ask for password
    const password = window.prompt(
      "To confirm account deletion, please enter your password:"
    );

    if (!password) {
      setError("Account deletion cancelled.");
      return;
    }

    // Final confirmation
    const finalConfirm = window.confirm(
      "🔴 FINAL CONFIRMATION\n\n" +
      "This is your last chance. Clicking OK will:\n" +
      "- Permanently delete your account\n" +
      "- Delete all monitored websites\n" +
      "- Delete all logs and history\n" +
      "- Cancel your subscription\n\n" +
      "Do you want to proceed?"
    );

    if (!finalConfirm) {
      setError("Account deletion cancelled.");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:4000/api/delete-account', {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({ password })
      });

      if (response.ok) {
        alert("Your account has been successfully deleted. You will now be logged out.");
        // Clear local storage and redirect
        localStorage.clear();
        window.location.href = '/';
      } else {
        const errorData = await response.json();
        setError(errorData.error || "Failed to delete account");
      }
    } catch (err) {
      setError("Failed to connect to server for account deletion");
      console.error("Delete account error:", err);
    } finally {
      setTimeout(() => setError(""), 5000);
    }
  };

  if (loading) {
    return (
      <DashboardLayout onLogout={handleLogout}>
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-emerald-500/20 border-t-emerald-500"></div>
          <span className="ml-2 text-gray-400">Loading settings...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
        <DashboardLayout onLogout={handleLogout}>
          <div className="max-w-4xl mx-auto space-y-4 sm:space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-white">Settings</h1>
          <p className="text-gray-400">Manage your account settings, notifications, and preferences</p>
        </div>

        {/* Notification Settings */}
        <NotificationSettings />

        {/* Test Notifications */}
        <NotificationTest />

        {/* Status Messages */}
        {message && (
          <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/20 p-4">
            <div className="text-sm text-emerald-400">{message}</div>
          </div>
        )}
        {error && (
          <div className="rounded-xl bg-red-500/10 border border-red-500/20 p-4">
            <div className="text-sm text-red-400">{error}</div>
          </div>
        )}

        {/* Monitoring Settings */}
        <Card>
          <Card.Header>
            <Card.Title>Monitoring Settings</Card.Title>
            <Card.Description>
              Configure how your websites are monitored
            </Card.Description>
          </Card.Header>

          <form onSubmit={handleSaveMonitoringSettings}>
            <Card.Content className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <label htmlFor="check_interval" className="block text-sm font-medium text-gray-400 mb-1">
                    Check Interval (seconds)
                  </label>
                      <input
                        id="check_interval"
                        name="check_interval"
                        type="number"
                        min="30"
                        max="3600"
                        value={settings.check_interval}
                        onChange={handleChange}
                        className="w-full bg-white/[0.04] border border-white/10 px-3 py-2.5 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all"
                      />
                  <p className="text-xs text-gray-500 mt-1">
                    How often to check your websites (minimum 30 seconds)
                  </p>
                </div>

                <div>
                  <label htmlFor="timeout_seconds" className="block text-sm font-medium text-gray-400 mb-1">
                    Timeout (seconds)
                  </label>
                  <input
                    id="timeout_seconds"
                    name="timeout_seconds"
                    type="number"
                    min="5"
                    max="60"
                    value={settings.timeout_seconds}
                    onChange={handleChange}
                        className="w-full bg-white/[0.04] border border-white/10 px-3 py-2.5 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Maximum time to wait for a response
                  </p>
                </div>
              </div>

              <div>
                    <label htmlFor="alert_cooldown" className="block text-sm font-medium text-gray-400 mb-1">
                  Alert Cooldown (seconds)
                </label>
                <input
                  id="alert_cooldown"
                  name="alert_cooldown"
                  type="number"
                  min="60"
                  max="3600"
                  value={settings.alert_cooldown}
                  onChange={handleChange}
                        className="w-full bg-white/[0.04] border border-white/10 px-3 py-2.5 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 focus:border-emerald-500/50 transition-all"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Minimum time between alerts for the same issue
                </p>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="performance_tracking"
                  name="performance_tracking"
                  type="checkbox"
                  checked={settings.performance_tracking}
                  onChange={handleChange}
                      className="h-4 w-4 text-emerald-500 focus:ring-emerald-500 border-white/20 bg-white/5 rounded"
                />
                    <label htmlFor="performance_tracking" className="text-sm font-medium text-gray-300">
                  Enable performance tracking
                </label>
              </div>

              <div className="flex items-center space-x-2">
                <input
                  id="uptime_history"
                  name="uptime_history"
                  type="checkbox"
                  checked={settings.uptime_history}
                  onChange={handleChange}
                      className="h-4 w-4 text-emerald-500 focus:ring-emerald-500 border-white/20 bg-white/5 rounded"
                />
                    <label htmlFor="uptime_history" className="text-sm font-medium text-gray-300">
                  Keep uptime history (30 days)
                </label>
              </div>
            </Card.Content>

            <Card.Footer>
              <Button type="submit" variant="primary" disabled={saving}>
                {saving ? "Saving..." : "Save Monitoring Settings"}
              </Button>
            </Card.Footer>
          </form>
        </Card>

        {/* Notification Settings */}
        <Card>
          <Card.Header>
            <Card.Title>Notification Settings</Card.Title>
            <Card.Description>
              Configure how you receive alerts
            </Card.Description>
          </Card.Header>

          <form onSubmit={handleSaveNotificationSettings}>
            <Card.Content className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-white">Email Notifications</h4>
                  <p className="text-sm text-gray-400">Receive email alerts for website issues</p>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="email_notifications"
                    name="email_notifications"
                    type="checkbox"
                    checked={settings.email_notifications}
                    onChange={handleChange}
                      className="h-4 w-4 text-emerald-500 focus:ring-emerald-500 border-white/20 bg-white/5 rounded"
                  />
                  <Badge variant={settings.email_notifications ? "success" : "danger"}>
                    {settings.email_notifications ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-white">Push Notifications</h4>
                  <p className="text-sm text-gray-400">Receive push notifications on your devices</p>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="push_notifications"
                    name="push_notifications"
                    type="checkbox"
                    checked={settings.push_notifications}
                    onChange={handleChange}
                      className="h-4 w-4 text-emerald-500 focus:ring-emerald-500 border-white/20 bg-white/5 rounded"
                  />
                  <Badge variant={settings.push_notifications ? "success" : "danger"}>
                    {settings.push_notifications ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-white">SMS Notifications</h4>
                  <p className="text-sm text-gray-400">Receive SMS alerts for critical issues</p>
                </div>
                <div className="flex items-center space-x-2">
                  <input
                    id="sms_notifications"
                    name="sms_notifications"
                    type="checkbox"
                    checked={settings.sms_notifications}
                    onChange={handleChange}
                      className="h-4 w-4 text-emerald-500 focus:ring-emerald-500 border-white/20 bg-white/5 rounded"
                  />
                  <Badge variant={settings.sms_notifications ? "success" : "warning"}>
                    {settings.sms_notifications ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
              </div>
            </Card.Content>

            <Card.Footer>
              <div className="flex space-x-3">
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? "Saving..." : "Save Notification Settings"}
                </Button>
                <Button type="button" variant="outline" onClick={handleExportData}>
                  Export Data
                </Button>
              </div>
            </Card.Footer>
          </form>
        </Card>

        {/* Data Export Section */}
        <Card>
          <Card.Header>
            <Card.Title>Data Export</Card.Title>
            <Card.Description>
              Download all your data in JSON format (GDPR compliant)
            </Card.Description>
          </Card.Header>
          
          <Card.Content>
            <div className="space-y-4">
              <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-4">
                <p className="text-sm text-gray-300">
                  Your export will include:
                </p>
                <ul className="mt-2 text-sm text-gray-400 space-y-1 ml-4 list-disc">
                  <li>Account information</li>
                  <li>Monitored websites</li>
                  <li>Settings and preferences</li>
                  <li>Monitoring logs (last 90 days)</li>
                  <li>Notification history</li>
                  <li>Payment history</li>
                </ul>
              </div>
            </div>
          </Card.Content>

          <Card.Footer>
            <Button type="button" variant="outline" onClick={handleExportData}>
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
              </svg>
              Download My Data
            </Button>
          </Card.Footer>
        </Card>

        {/* Account Deletion Section */}
        <Card>
          <Card.Header>
            <Card.Title className="text-red-600 dark:text-red-400">Danger Zone</Card.Title>
            <Card.Description>
              Irreversible actions that will permanently affect your account
            </Card.Description>
          </Card.Header>
          
          <Card.Content>
            <div className="space-y-4">
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4">
                <h4 className="text-sm font-medium text-red-400 mb-2">
                  Delete Account
                </h4>
                <p className="text-sm text-red-300/80 mb-3">
                  Once you delete your account, there is no going back. This action will permanently delete:
                </p>
                <ul className="text-sm text-red-400/80 space-y-1 ml-4 list-disc">
                  <li>Your account and profile</li>
                  <li>All monitored websites</li>
                  <li>All monitoring logs and history</li>
                  <li>All settings and preferences</li>
                  <li>All notification settings</li>
                  <li>Your subscription (if active)</li>
                </ul>
              </div>
            </div>
          </Card.Content>

          <Card.Footer>
            <Button 
              type="button" 
              variant="danger"
              onClick={handleDeleteAccount}
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Delete My Account
            </Button>
          </Card.Footer>
        </Card>


      </div>
    </DashboardLayout>
  );
}
